import React, { useState, useEffect } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { colors } from '../components/Theme/WebTheme';
import {
  Switch, FormControlLabel, Typography, TextField, Grid, Divider,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SECONDORY_COLOR, MAIN_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import BlankTable from '../components/Table/BlankTable';
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import Button from "components/CustomButtons/Button.js";

const useStyles = makeStyles(() => ({
  typography: { fontFamily: FONT_FAMILY },
  textField: { fontFamily: FONT_FAMILY, width: '100%' },
  rootRtl: {
    "& label": { right: 10, left: "auto", paddingRight: 20, fontFamily: FONT_FAMILY },
    "& legend": { textAlign: "right", marginRight: 20 },
    "& label.Mui-focused": { color: MAIN_COLOR },
    "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: MAIN_COLOR } },
    "& input": { fontFamily: FONT_FAMILY },
  },
}));

export default function Promos() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const classes = useStyles();
  const { editPromo, editSettings } = api;
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();

  // ─── Promo table state ───────────────────────────────────────────────────────
  const columns = [
    { title: t('promo_name'), field: 'promo_name' },
    { title: t('promo_code_web'), field: 'promo_code' },
    { title: t('description'), field: 'promo_description' },
    { title: t('type'), field: 'promo_discount_type', lookup: { flat: t('flat'), percentage: t('percentage') } },
    { title: t('promo_discount_value'), field: 'promo_discount_value', type: 'numeric' },
    { title: t('max_limit'), field: 'max_promo_discount_value', type: 'numeric' },
    { title: t('min_limit'), field: 'min_order', type: 'numeric' },
    { title: t('end_date'), field: 'promo_validity', render: rowData => rowData.promo_validity ? moment(rowData.promo_validity).format('lll') : null },
    { title: t('promo_usage'), field: 'promo_usage_limit', type: 'numeric' },
    {
      title: t('show_in_list'), field: 'promo_show', type: 'boolean', render: (rowData) => (
        <Switch
          checked={rowData.promo_show}
          onChange={() => handelShowPromo(rowData)}
          disabled={!settings.AllowCriticalEditsAdmin}
        />
      ),
    },
    { title: t('promo_used_by'), field: 'user_avail', editable: 'never' },
  ];

  const [data, setData] = useState([]);
  const promodata = useSelector(state => state.promodata);
  const [sortedData, SetSortedData] = useState([]);
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const auth = useSelector(state => state.auth);
  const [selectedRow, setSelectedRow] = useState(null);
  const { state } = useLocation();
  const [currentPage, setCurrentPage] = useState(null);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) setRole(auth.profile.usertype);
  }, [auth.profile]);

  useEffect(() => { setCurrentPage(state?.pageNo); }, [state]);

  const HandalePageChange = (page) => { setCurrentPage(page); };

  useEffect(() => {
    if (promodata.promos) setData(promodata.promos);
    else setData([]);
  }, [promodata.promos]);

  useEffect(() => {
    if (data) SetSortedData(data.sort((a, b) => (moment(b.createdAt) - moment(a.createdAt))));
  }, [data]);

  const handelShowPromo = (rowData) => {
    if (role === "admin") dispatch(editPromo({ ...rowData, promo_show: !rowData.promo_show }, "Update"));
  };

  // ─── Cashback settings state ──────────────────────────────────────────────────
  const [cb, setCb] = useState(null);

  useEffect(() => {
    if (settings) setCb(settings);
  }, [settings]);

  const handleCbSwitch = (e) => {
    setCb(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const handleCbText = (e) => {
    setCb(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCbMode = (e) => {
    setCb(prev => ({ ...prev, cashbackMode: e.target.value }));
  };

  const handleCbSave = () => {
    if (!settings.AllowCriticalEditsAdmin) { alert(t('demo_mode')); return; }
    if (cb.cashbackEnabled && (!cb.cashbackAmountMRU || parseFloat(cb.cashbackAmountMRU) <= 0)) {
      alert('Cashback amount must be greater than 0.'); return;
    }
    if (cb.cashbackEnabled && cb.cashbackMode === 'limited') {
      if (!cb.cashbackStartDate || !cb.cashbackEndDate) {
        alert('Please set both start and end date for Limited mode.'); return;
      }
      if (new Date(cb.cashbackEndDate) <= new Date(cb.cashbackStartDate)) {
        alert('End date must be after start date.'); return;
      }
    }
    dispatch(editSettings(cb));
    alert('Cashback settings saved.');
  };

  const cbEnabled = cb?.cashbackEnabled || false;
  const cbMode = cb?.cashbackMode || 'always';

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    promodata.loading ? <CircularLoading /> :
      <>
        <ThemeProvider theme={theme}>
          {sortedData?.length > 0 ?
            <MaterialTable
              title={t('promo_offer_title')}
              columns={columns}
              onChangePage={(page) => HandalePageChange(page)}
              style={{
                direction: isRTL === "rtl" ? "rtl" : "ltr",
                borderRadius: "8px",
                boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
                padding: "20px",
              }}
              data={sortedData}
              onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
              options={{
                initialPage: state?.pageNo,
                rowStyle: (rowData) => ({
                  backgroundColor: selectedRow === rowData.tableData.id ? colors.ROW_SELECTED : colors.WHITE,
                }),
                ...TableStyle()
              }}
              localization={localization(t)}
              editable={{
                onRowDelete: oldData =>
                  settings.AllowCriticalEditsAdmin ?
                    new Promise(resolve => { setTimeout(() => { resolve(); dispatch(editPromo(oldData, "Delete")); }, 600); })
                    :
                    new Promise(resolve => { setTimeout(() => { resolve(); alert(t('demo_mode')); }, 600); })
              }}
              actions={[
                {
                  icon: 'add',
                  tooltip: t("add_promo_title"),
                  isFreeAction: true,
                  onClick: () => navigate(`/promos/editpromo`, { state: { pageNo: currentPage } })
                },
                (rowData) => ({
                  tooltip: t("edit"),
                  icon: () => (
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", marginRight: 0 }}>
                      <EditIcon />
                    </div>
                  ),
                  onClick: () => navigate(`/promos/editpromo/${rowData.id}`, { state: { pageNo: currentPage } })
                }),
              ]}
            />
            : <BlankTable
              title={t('promo_offer_title')}
              actions={[{ icon: 'add', tooltip: t("add_promo_title"), isFreeAction: true, onClick: () => navigate(`/promos/editpromo`) }]}
              columns={columns} data={[]} localization={localization(t)} options={TableStyle()}
            />
          }
        </ThemeProvider>

        {/* ── Cashback Settings ─────────────────────────────────────────────── */}
        {cb &&
          <div style={{
            marginTop: '32px',
            borderRadius: '8px',
            boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
            padding: '30px',
            backgroundColor: colors.WHITE,
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          }}>
            <Divider style={{ marginBottom: '20px' }} />
            <Typography component="h1" variant="h5" style={{ fontFamily: FONT_FAMILY, marginBottom: '16px', textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
              Cashback Settings
            </Typography>

            {/* Global ON/OFF */}
            <FormControlLabel
              style={{ flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}
              control={
                <Switch
                  checked={cbEnabled}
                  onChange={handleCbSwitch}
                  name="cashbackEnabled"
                  color="primary"
                />
              }
              label={<Typography className={classes.typography}>Enable Ride Cashback</Typography>}
            />

            {cbEnabled && (
              <Grid container spacing={2} style={{ marginTop: '8px' }}>

                {/* Cashback Mode */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel style={{ fontFamily: FONT_FAMILY }}>Cashback Mode</InputLabel>
                    <Select
                      value={cbMode}
                      onChange={handleCbMode}
                      label="Cashback Mode"
                      style={{ fontFamily: FONT_FAMILY }}
                    >
                      <MenuItem value="always">Always ON (unlimited period)</MenuItem>
                      <MenuItem value="weekend">Weekend Only</MenuItem>
                      <MenuItem value="limited">Limited Date / Time Range</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Amount per ride */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={isRTL === 'rtl' ? classes.rootRtl : classes.textField}
                    InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                    variant="outlined"
                    fullWidth
                    type="number"
                    label="Cashback Amount per Ride (MRU)"
                    name="cashbackAmountMRU"
                    value={cb.cashbackAmountMRU || ''}
                    onChange={handleCbText}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* Min ride fare */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={isRTL === 'rtl' ? classes.rootRtl : classes.textField}
                    InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                    variant="outlined"
                    fullWidth
                    type="number"
                    label="Min Eligible Ride Fare (MRU)"
                    name="minRideFareMRU"
                    value={cb.minRideFareMRU || ''}
                    onChange={handleCbText}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* Daily cap: rides */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={isRTL === 'rtl' ? classes.rootRtl : classes.textField}
                    InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                    variant="outlined"
                    fullWidth
                    type="number"
                    label="Max Cashback Rides per User per Day"
                    name="maxCashbackRidesPerUserPerDay"
                    value={cb.maxCashbackRidesPerUserPerDay || ''}
                    onChange={handleCbText}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* Daily cap: amount */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    className={isRTL === 'rtl' ? classes.rootRtl : classes.textField}
                    InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                    variant="outlined"
                    fullWidth
                    type="number"
                    label="Max Cashback per User per Day (MRU)"
                    name="maxCashbackPerUserPerDayMRU"
                    value={cb.maxCashbackPerUserPerDayMRU || ''}
                    onChange={handleCbText}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* Limited date range */}
                {cbMode === 'limited' && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        className={isRTL === 'rtl' ? classes.rootRtl : classes.textField}
                        InputLabelProps={{ style: { fontFamily: FONT_FAMILY }, shrink: true }}
                        variant="outlined"
                        fullWidth
                        type="datetime-local"
                        label="Cashback Start Date / Time"
                        name="cashbackStartDate"
                        value={cb.cashbackStartDate || ''}
                        onChange={handleCbText}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        className={isRTL === 'rtl' ? classes.rootRtl : classes.textField}
                        InputLabelProps={{ style: { fontFamily: FONT_FAMILY }, shrink: true }}
                        variant="outlined"
                        fullWidth
                        type="datetime-local"
                        label="Cashback End Date / Time"
                        name="cashbackEndDate"
                        value={cb.cashbackEndDate || ''}
                        onChange={handleCbText}
                      />
                    </Grid>
                  </>
                )}

                {/* Payment type switches */}
                <Grid item xs={12}>
                  <FormControlLabel
                    style={{ flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row', marginRight: '24px' }}
                    control={
                      <Switch
                        checked={cb.cashbackForWalletPaymentsEnabled !== false}
                        onChange={handleCbSwitch}
                        name="cashbackForWalletPaymentsEnabled"
                        color="primary"
                      />
                    }
                    label={<Typography className={classes.typography}>Cashback for Wallet-Paid Rides</Typography>}
                  />
                  <FormControlLabel
                    style={{ flexDirection: isRTL === 'rtl' ? 'row-reverse' : 'row' }}
                    control={
                      <Switch
                        checked={cb.cashbackForCashPaymentsEnabled || false}
                        onChange={handleCbSwitch}
                        name="cashbackForCashPaymentsEnabled"
                        color="primary"
                      />
                    }
                    label={<Typography className={classes.typography}>Cashback for Cash-Paid Rides</Typography>}
                  />
                </Grid>
              </Grid>
            )}

            <div style={{ marginTop: '24px' }}>
              <Button
                size="lg"
                onClick={handleCbSave}
                variant="contained"
                color="secondaryButton"
              >
                <Typography style={{ fontFamily: FONT_FAMILY, fontSize: '14px' }}>Save Cashback Settings</Typography>
              </Button>
            </div>
          </div>
        }
      </>
  );
}
